import { QueryCache } from "@tanstack/react-query";
import { GeneratedSlot } from "./api";
import { slotValue } from "./editor/blanks";
import { ProposalDocument, ProposalSlot, SlotValues } from "./types";

export const toProposalSlot = (slot: GeneratedSlot): ProposalSlot => ({
  id: slot.id,
  sectionId: slot.section,
  kind: slot.kind,
  label: slot.label,
  value: slot.value,
  multiline: slot.multiline,
  maxLength: slot.maxLength ?? undefined,
  boxes: slot.boxes,
  record: slot.record ?? undefined,
  readOnly: slot.readOnly,
  readOnlyNote: slot.readOnlyNote ?? undefined,
  required: slot.required,
  missing: slot.missing ?? undefined
});

// What the composer draws in each slot: Generate's value, or what the rep typed
// over it. An empty slot goes as null, which the composer marks as a blank.
export const composeValues = (
  slots: ProposalSlot[],
  typed: SlotValues
): Record<string, string | null> =>
  Object.fromEntries(
    slots.map((slot) => [slot.id, slotValue(slot, typed) || null])
  );

// A document's PDF lives at a local URL until nothing shows it any more.
export const releaseDocumentUrl = (url: string | undefined) => {
  if (url?.startsWith("blob:")) URL.revokeObjectURL(url);
};

const watchedPrefixes = new WeakMap<QueryCache, Set<string>>();

// A cached document's PDFs are released once the cache drops that entry. A
// refetch doesn't release the one it replaced: an editor already open keeps
// showing and downloading it. Watched once per cache and prefix, so it keeps
// watching after the editor closes.
export const releaseDocumentUrlsWithCache = (
  cache: QueryCache,
  queryKeyPrefix: string
) => {
  const prefixes = watchedPrefixes.get(cache) ?? new Set<string>();
  if (prefixes.has(queryKeyPrefix)) return;
  prefixes.add(queryKeyPrefix);
  watchedPrefixes.set(cache, prefixes);
  const urlsByQuery = new Map<string, Set<string>>();
  cache.subscribe((event) => {
    if (event.query.queryKey[0] !== queryKeyPrefix) return;
    const hash = event.query.queryHash;
    if (event.type === "removed") {
      urlsByQuery.get(hash)?.forEach(releaseDocumentUrl);
      urlsByQuery.delete(hash);
      return;
    }
    // A document and a preview both carry their PDF here.
    const url = (
      event.query.state.data as
        | Pick<ProposalDocument, "documentUrl">
        | undefined
    )?.documentUrl;
    if (!url) return;
    const urls = urlsByQuery.get(hash) ?? new Set<string>();
    urls.add(url);
    urlsByQuery.set(hash, urls);
  });
};
